import { track, identify, Identify, experiment } from "@amplitude/unified";

const ORG_STORAGE_KEY = "amplitude_org";
const SUPER_ORG_STORAGE_KEY = "amplitude_super_org";
const SPECIALTY_STORAGE_KEY = "amplitude_specialty";
// Fired once the feature flags have been fetched again for the identified
// user; useFeatureGate re-reads its variant on it.
export const FEATURE_GATES_REFRESHED_EVENT = "knowtex:feature-gates-refreshed";
let lastIdentifiedProperties = null;


/**
 * Event names for Amplitude tracking.
 * Use these instead of raw strings to ensure consistency.
 */
export const Events = {
  AUDIO_UPLOAD_COMPLETED: "Audio Upload Completed",
  PATIENT_REVIEW_INITIATED: "Patient Review Initiated",
  PATIENT_NOTE_COPIED: "Patient Note Copied",
  PATIENT_NOTE_FINALIZED: "Patient Note Finalized",
  NOTE_TITLE_SELECTED: "Note Title Selected",
  SPECIALTY_SWAPPED: "Specialty Swapped",
  LANGUAGE_PROMPT_ANSWERED: "Language Prompt Answered",
  LANGUAGE_PROMPT_DISMISSED: "Language Prompt Dismissed",
};

/**
 * Track an event with optional properties.
 * Automatically includes deployment: "gov" on all events.
 * @param {string} eventName - Event name (use Events object)
 * @param {object} properties - Optional event properties (use snake_case)
 */
export function trackEvent(eventName, properties = {}, options = {}) {
  const context = getAnalyticsContext();

  track(
    eventName,
    {
      ...context,
      ...properties,
      deployment: process.env.REACT_APP_AMPLITUDE_ENV,
    },
    options,
  );
}

/**
 * Identify the current user with properties.
 * Call this after login to set user properties like org, specialty, etc.
 * @param {object} properties - User properties to set
 */
export function identifyUser(properties = {}) {
  const identifyObj = new Identify();

  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identifyObj.set(key, value);
    }
  });

  identify(identifyObj);
  persistAnalyticsContext(properties);

  // Flags targeted on user properties resolve to the anonymous variant when
  // fetched before the user is identified, so fetch them again now — once for
  // the whole app, and only when the properties actually changed.
  const serialized = JSON.stringify(properties);
  if (serialized !== lastIdentifiedProperties) {
    lastIdentifiedProperties = serialized;
    refreshFeatureGates();
  }
}

async function refreshFeatureGates() {
  const client = experiment();
  if (!client) return;
  try {
    await client.fetch();
  } catch (err) {
    console.error("[FeatureGate] fetch failed:", err);
    // Forget the attempt so the next identify tries again.
    lastIdentifiedProperties = null;
    return;
  }
  window.dispatchEvent(new Event(FEATURE_GATES_REFRESHED_EVENT));
}

function persistAnalyticsContext(properties = {}) {
  if (properties.org !== undefined && properties.org !== null) {
    localStorage.setItem(ORG_STORAGE_KEY, String(properties.org));
  }

  if (properties.super_org !== undefined && properties.super_org !== null) {
    localStorage.setItem(SUPER_ORG_STORAGE_KEY, String(properties.super_org));
  }

  if (properties.specialty !== undefined && properties.specialty !== null) {
    localStorage.setItem(SPECIALTY_STORAGE_KEY, String(properties.specialty));
  }

}

export function getAnalyticsContext() {
  const org = localStorage.getItem(ORG_STORAGE_KEY);
  const superOrg = localStorage.getItem(SUPER_ORG_STORAGE_KEY);
  const specialty = localStorage.getItem(SPECIALTY_STORAGE_KEY);
  return {
    ...(org ? { org } : {}),
    ...(superOrg ? { super_org: superOrg } : {}),
    ...(specialty ? { specialty } : {}),
  };
}

export function clearAnalyticsContext() {
  localStorage.removeItem(ORG_STORAGE_KEY);
  localStorage.removeItem(SUPER_ORG_STORAGE_KEY);
  localStorage.removeItem(SPECIALTY_STORAGE_KEY);
}
